import { useEffect } from 'react';
import { getProductPath } from '../utils/productUrls';

const SITE_URL = 'https://www.valleyintlsupply.com';
const SITE_NAME = 'Valley International Supply';
const DEFAULT_IMAGE = `${SITE_URL}/logo.png`;

function setMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
}

function cleanDescription(value, fallback) {
  const text = String(value || fallback).replace(/\s+/g, ' ').trim();
  if (text.length <= 160) return text;
  const shortened = text.slice(0, 157);
  return `${shortened.slice(0, shortened.lastIndexOf(' '))}...`;
}

export default function BrandSeo({ brand, products = [] }) {
  useEffect(() => {
    if (!brand?.brand_name || !brand?.idbrand) return undefined;

    const canonical = `${SITE_URL}/brands/${encodeURIComponent(brand.idbrand)}`;
    const title = `${brand.brand_name} Industrial Products | Valley Supply`.slice(0, 65);
    const description = cleanDescription(
      brand.brand_description,
      `Browse ${brand.brand_name} industrial automation products, availability and product details from Valley International Supply.`
    );

    document.title = title;
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonical);
    setMeta('meta[name="description"]', { name: 'description', content: description });
    setMeta('meta[name="robots"]', { name: 'robots', content: 'index, follow, max-image-preview:large' });
    setMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    setMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    setMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    setMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    setMeta('meta[property="og:image"]', { property: 'og:image', content: DEFAULT_IMAGE });
    setMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: DEFAULT_IMAGE });
    document.getElementById('valley-webpage-schema')?.remove();

    const productItems = products
      .filter((product) => product?.product_name)
      .map((product, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${SITE_URL}${getProductPath(product, { trailingSlash: true })}`,
        name: product.product_name
      }));

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          '@id': `${canonical}#webpage`,
          url: canonical,
          name: title,
          description,
          inLanguage: 'en-US',
          isPartOf: {
            '@type': 'WebSite',
            '@id': `${SITE_URL}/#website`,
            name: SITE_NAME,
            url: `${SITE_URL}/`
          },
          breadcrumb: { '@id': `${canonical}#breadcrumb` },
          mainEntity: productItems.length ? { '@id': `${canonical}#products` } : undefined
        },
        {
          '@type': 'Brand',
          '@id': `${canonical}#brand`,
          name: brand.brand_name,
          description
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `${canonical}#breadcrumb`,
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
            { '@type': 'ListItem', position: 2, name: 'Brands', item: `${SITE_URL}/brands` },
            { '@type': 'ListItem', position: 3, name: brand.brand_name, item: canonical }
          ]
        },
        ...(productItems.length ? [{
          '@type': 'ItemList',
          '@id': `${canonical}#products`,
          name: `${brand.brand_name} products`,
          numberOfItems: productItems.length,
          itemListElement: productItems
        }] : [])
      ]
    };

    let script = document.getElementById('valley-brand-schema');
    if (!script) {
      script = document.createElement('script');
      script.id = 'valley-brand-schema';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    return () => document.getElementById('valley-brand-schema')?.remove();
  }, [brand, products]);

  return null;
}
